import Tag from "./Tag.js";
import store from "./store.js";

let serverErrorToastActive = false;
export function clearServerErrorToastFlag() {
  serverErrorToastActive = false;
}

export default class Session {
  constructor(hostname) {
    this.hostname = hostname
    this.sessionId = parseInt(Math.random() * 1000);
    
    this.isConnected = false;
    this.didConnectionFail = false;
    this.ajaxQueue = [];
    this.timedOut = false;
    this.retryTimer = null;
    this.pollAppTimer = null;
    this.pollAppTimerElapsed = false;
    this.ajaxTimeoutTimer = null;
    this.currentPendingRequest = null;
    this.programStartTime = JSON.parse(localStorage.getItem(`programStartTime[${this.hostname}]`));
    
    this.system = null;
    this.systemCopy = null;
    this.templates = null;
    this.technicianMenu = null;
    this.controlPaths = null;
    this.pollingParams = [];
  }
  
  onAjaxTimeout() {
    this.timedOut = true;
    let didAllSessionsTimeout = store.sessions.every(session => session.timedOut);
    let didSessionZeroTimeout = store.sessions[0].timedOut;
    if (didAllSessionsTimeout || didSessionZeroTimeout)
      window.chrome?.webview?.postMessage("ajaxTimeout");
  }
  
  onAjaxReestablished() {
    window.chrome?.webview?.postMessage("ajaxReestablished");
  }

  loadApp(callback) {
    this.queueRequest("/cmd/loadApp", null, response => {
      if (this.closed) return;
      if (this.checkForReboot(response)) return;
      
      this.isConnected = true;
      this.didConnectionFail = false;
      Object.keys(response).forEach(key => {
        if (key == "tags")
          this.system = new Tag(this, null, response.tags[0]);
        else
          this[key] = response[key];
      });
      localStorage.setItem(`programStartTime[${this.hostname}]`, this.programStartTime);
      
      if (callback) {
        callback(response);
      }
    }, true, 0);
  }

  checkForReboot(response) {
    // Reload the page if the board has rebooted.
    if (this.programStartTime != null && this.programStartTime != response.programStartTime) {
      // Clear all programStartTime values from localStorage.
      let keys = Object.keys(localStorage).filter(key => key.match(/programStartTime\[.*\]/g));
      keys.forEach(key => localStorage.removeItem(key));
      // Reload the page.
      location.reload();
      return true;
    }
    return false;
  }

  startPollingFor(name, args, responseHandler) {
    this.stopPollingFor(name);
    this.pollingParams.push({
      name,
      args,
      responseHandler
    });
    this.pollApp();
  }

  stopPollingFor(name) {
    this.pollingParams = this.pollingParams.filter(item => item.name != name);
  }

  pollApp(callback, interval) {
    clearTimeout(this.ajaxTimeoutTimer);
    this.ajaxTimeoutTimer = setTimeout(() => this.onAjaxTimeout(), 15000);

    let pollingParams = this.pollingParams.slice();
    let request = this.queueRequest("/cmd/pollApp", { pollingParams }, response => {
      clearTimeout(this.ajaxTimeoutTimer);
      if (this.timedOut) {
        this.timedOut = false;
        this.onAjaxReestablished();
      }
      
      if (this.checkForReboot(response))
        return;
      
      this.updateTags(response.tags);
      pollingParams.forEach(param => {
        let result = response.paramResults[param.name];
        param.responseHandler(result);
      });

      if (callback) {
        callback();
      }
      if (interval && this.pollAppTimerElapsed) {
        this.pollApp(null, interval);
      }
    }, true);

    if (interval) {
      clearTimeout(this.pollAppTimer);
      this.pollAppTimerElapsed = false;
      this.pollAppTimer = setTimeout(() => {
        this.pollAppTimerElapsed = true;
        if (request.completed) this.pollApp(callback, interval);
      }, interval);
    }
  }

  changeValues(values, callback) {
    values = values.map(value => {
      if (value instanceof Tag)
        return { handle: parseInt(value.handle), value: value.value };
      else
        return value;
    });

    this.queueRequest("/cmd/changeValues", { values }, response => {
      this.updateTags(response.tags);
      if (callback)
        callback();
    });
  }

  changeSettings(settings, callback) {
    settings = settings.map(setting => {
      if (setting instanceof Tag)
        return { handle: parseInt(setting.handle), value: setting.value };
      else
        return setting;
    });

    this.queueRequest("/cmd/changeSettings", { settings }, response => {
      this.updateTags(response.tags);
      if (callback)
        callback();
    });
  }

  toggleOptionalSettings(settings, callback) {
    this.queueRequest("/cmd/toggleOptionalSettings", { settings }, response => {
      this.updateTags(response.settings);
      if (callback)
        callback();
    });    
  }

  updateTags(tagsJson) {
    tagsJson.forEach(tagJson => {
      let tag = this.system.$handleMap[tagJson.handle];
      tag.$update(tagJson);
    });
  }

  // Requests are queued such that the next request isn't sent until the previous 
  // request is completed. This is done because there is no way  to control the 
  // completion order of two simultaneous requests: either request could arrive at
  // the server first. TODO: Consider allowing the caller to indicate when it is OK 
  // to send two or more specific requests simultaneously.
  queueRequest(path, requestBody, callback, retryEnabled = false, queueIndex = null) {
    let session = this;
    let request = {
      path,
      requestBody,
      callback,
      retryEnabled,
      completed: false,
      cancelled: false,
      cancel() {
        this.cancelled = true;
        if (this == session.currentPendingRequest)
          session.currentPendingRequest = null;
      }
    };
    if (queueIndex == null)
      session.ajaxQueue.push(request);
    else
      session.ajaxQueue.splice(queueIndex, 0, request);

    session.trySendNextRequest();
    return request;
  }

  async trySendNextRequest() {
    if (this.closed) throw "Session closed";
    if (this.currentPendingRequest) return;

    this.ajaxQueue = this.ajaxQueue.filter(request => !request.cancelled);
    if (!this.ajaxQueue.length) return;

    let request = this.ajaxQueue.shift();
    this.currentPendingRequest = request;

    try {
      let url = `http://${this.hostname}${request.path}`;
      let response = await fetch(url, {
        mode: "cors",
        method: "post",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: this.sessionId,
          ... request.requestBody
        })
      });

      if (this.closed)
        return;
      
      if (response.ok) {
        if (request.callback && !request.cancelled) {
          let responseText = await response.text();
          let responseJson = responseText ? JSON.parse(responseText) : null;
          request.callback(responseJson);
        }
        if (request == this.currentPendingRequest) {
          this.currentPendingRequest = null;
        }
        blinkNetworkIcon();
        this.trySendNextRequest();
      }
      else {
        let responseText = await response.text();
        let errorMessage = responseText ? `HTTP ${response.status} ${response.statusText}\n\n ${responseText}` : `HTTP ${response.status} ${response.statusText}`;
        if (response.status >= 500) {
          if (!serverErrorToastActive) {
            serverErrorToastActive = true;
            store.toast.add({ severity: "error", summary: "Request failed", detail: String(errorMessage), context: "server-error" });
          }
        }
        throw new Error(errorMessage);
      }
      request.completed = true;
    }
    catch (error) {
      // Log the error.
      console.error(error);
      // Nothing left to do if this session has been closed.
      if (this.closed) return;
      // Something is wrong if the failed request isn't the request we most recently sent.
      console.assert(this.currentPendingRequest == request);
      // Indicate that there is no longer a pending request.
      this.currentPendingRequest = null;
      // Introduce a short delay before sending the next request.
      this.retryTimer = setTimeout(() => {
        if (this.closed) {
          // Don't retry if the session has been closed.
          return;
        }
        else if (request.cancelled)  {
          // Don't retry requests that have been cancelled.
          return;
        }
        else if (request.path == '/cmd/loadApp') {
          // Just retry the request if it was for /cmd/loadApp.
          this.didConnectionFail = true;
          this.ajaxQueue.unshift(request);
          this.ajaxQueue = this.ajaxQueue.filter(request => request.retryEnabled);
          this.trySendNextRequest();
        }
        else {
          // Otherwise, reload the application state. This needs to be done because we may have missed some updates if the error was due to connection failure.
          this.loadApp(() => {
            // Now retry the original request if retry is enabled.
            this.ajaxQueue.unshift(request); 
            this.ajaxQueue = this.ajaxQueue.filter(request => request.retryEnabled);
            this.trySendNextRequest();
          });
        }
      }, 1000);
    }
  }

  close() {
    clearTimeout(this.retryTimer);
    clearTimeout(this.pollAppTimer);
    clearTimeout(this.ajaxTimeoutTimer);
    this.currentPendingRequest?.cancel();
    this.ajaxQueue.forEach(request => request.cancel());
    this.closed = true;
  }
}

let iconTimeout = null;
function blinkNetworkIcon() {
  let icon = document.querySelector(".networkBlinker");
  if (icon) {
    icon.style.color = "lawngreen";
    icon.style.opacity = "0.5";
    clearTimeout(iconTimeout);
    iconTimeout = setTimeout(() => {
      icon.style.opacity = "0.1";
      icon.style.color = "white"; 
    }, 150);
  }
}